import { useState, readonly, computed, useEventListener, useTimeoutFn, nextTick, useCoreConfig, useDayjs, createSharedComposable, tryOnScopeDispose } from '#imports'

export interface ConsentCategories {
    necessary: boolean
    preferences: boolean
    statistics: boolean
    marketing: boolean
}

const EMPTY_CONSENT: ConsentCategories = {
    necessary: false,
    preferences: false,
    statistics: false,
    marketing: false,
}

/** How long the CMP gets to load before consent-gated UI stops waiting for it, in ms. Matches `whenLoaded`. */
const UNAVAILABLE_AFTER = 5_000

const CONSENT_KEYS = Object.keys(EMPTY_CONSENT) as Array<keyof ConsentCategories>

/** Window global per provider. Both CMPs emit `<global>On{ConsentReady,Accept,Decline}` events. */
const CMP_GLOBAL = {
    cookiebot: 'Cookiebot',
    cookieguard: 'CookieGuard',
} as const

/**
 * Provider-agnostic consent facade over whichever CMP `core.consent.provider` selects.
 * Read and drive consent through this, never `window.Cookiebot` / `window.CookieGuard` directly.
 */
export const useCookieConsent = createSharedComposable(() => {
    const provider = useCoreConfig().consent?.provider ?? 'cookiebot'
    const dayjs = useDayjs()
    const cmpGlobal = CMP_GLOBAL[provider]
    const cmp = () => {
        // Without a window (the page environment is gone) there is no CMP to read.
        if (typeof window === 'undefined') return undefined
        const current = window[cmpGlobal]
        // The head loader's `<script id="Cookiebot">` makes `window.Cookiebot` that element until uc.js replaces it.
        return current instanceof Element ? undefined : current
    }
    // `window.CookieGuard` exists from bundle evaluation; only `initialized` says init() has run.
    const isLoaded = () => (provider === 'cookieguard' ? cmp()?.initialized === true : Boolean(cmp()))

    const isConsentReady = useState<boolean>('consent:is-ready', () => false)
    const consent = useState<ConsentCategories>('consent:categories', () => ({ ...EMPTY_CONSENT }))
    const isUnavailable = useState<boolean>('consent:is-unavailable', () => false)

    const sync = () => {
        const current = cmp()
        if (!current) return
        if (isLoaded()) isUnavailable.value = false
        isConsentReady.value = Boolean(current.hasResponse)
        const next = { ...EMPTY_CONSENT }
        for (const key of CONSENT_KEYS) next[key] = Boolean(current.consent?.[key])
        if (CONSENT_KEYS.some(key => next[key] !== consent.value[key])) consent.value = next
    }

    /** The visitor has answered the banner and allowed no non-necessary category. */
    const isDeclined = computed(() =>
        isConsentReady.value && !consent.value.preferences && !consent.value.statistics && !consent.value.marketing,
    )

    /**
     * Resolves once the CMP is ready to use, `false` if it is not within `timeout` ms.
     * Cookiebot is loaded asynchronously via GTM; CookieGuard is ready once `init()` has run.
     */
    // Polling ends with the composable's scope as well as at its deadline.
    let disposed = false
    tryOnScopeDispose(() => {
        disposed = true
    })

    const whenLoaded = async (timeout = UNAVAILABLE_AFTER) => {
        if (!import.meta.client) return false
        const deadline = dayjs().add(timeout, 'millisecond')
        while (!disposed && !isLoaded() && dayjs().isBefore(deadline)) {
            await new Promise(resolve => setTimeout(resolve, 100))
        }
        const loaded = isLoaded()
        if (loaded) sync()
        return loaded
    }

    /**
     * The visitor has answered the banner, or the CMP failed to load and no answer will come.
     * Gate consent-dependent UI on this rather than `isConsentReady`, so it still surfaces when the CMP is blocked.
     */
    const isConsentResolved = computed(() => isConsentReady.value || isUnavailable.value)

    /** Open the banner. */
    const show = () => {
        if (import.meta.client) cmp()?.show()
    }

    /** Close the banner without recording a decision. */
    const hide = () => {
        if (import.meta.client) cmp()?.hide()
    }

    /** Record a decline of every non-necessary category and close the banner. */
    const declineAll = () => {
        if (!import.meta.client) return
        if (provider === 'cookieguard') {
            cmp()?.rejectAll()
            return
        }
        cmp()?.submitCustomConsent(false, false, false, false)
        cmp()?.hide()
    }

    /** Reopen the banner so the visitor can change a settled decision without discarding it. */
    const renew = () => {
        if (!import.meta.client) return
        if (provider === 'cookieguard') cmp()?.show()
        else cmp()?.renew()
    }

    if (import.meta.client) {
        for (const event of ['ConsentReady', 'Accept', 'Decline']) {
            useEventListener(window, `${cmpGlobal}On${event}`, sync)
        }
        sync()
        // useEventListener attaches with flush: "post"; an event fired before that is missed.
        nextTick(sync)
        // Tracking protection (e.g. Firefox ETP) can block the CMP script, so its events never fire.
        useTimeoutFn(async () => {
            if (isLoaded()) return
            isUnavailable.value = true
            // An unanswered CMP that loads late fires no consent event, so keep polling for it.
            if (await whenLoaded(60_000)) isUnavailable.value = false
        }, UNAVAILABLE_AFTER)
    }

    return {
        isConsentReady: readonly(isConsentReady),
        /** Alias of `isConsentReady`: the visitor has answered the banner (accept or decline). */
        hasResponse: readonly(isConsentReady),
        isDeclined,
        /** The CMP did not load within the `whenLoaded` timeout; cleared if it turns up within a minute after that. */
        isUnavailable: readonly(isUnavailable),
        isConsentResolved,
        consent: readonly(consent),
        whenLoaded,
        show,
        hide,
        declineAll,
        renew,
    }
})
